import Image from 'components/Shared/Image';
import { ListViewProduct } from 'models/ListViewProduct';
import styles from 'components/Shared/Product/styles.scss';
import { rm, emptyFunction, NO_ELEMENT } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import cn from 'classnames';
import { NotifyLink } from 'components/Shared/NotifyLink';
import Splash from 'components/Shared/Splash';
import Brand from 'components/Shared/Brand';
import { Component, Fragment } from 'react';
import ProductCardFlag from 'components/ProductCardFlag';
import newProductImage from 'assets/img/sprite/newproduct.svg';
import { EnergyArrow } from 'components/Energy/arrow';
import { IEnergyDeclaration } from 'models/IEnergyLabel';
import { Price as PriceModel } from 'models/Price';
import { TopTested } from 'components/Shared/Product/topTested';
import { getIsTopTested } from 'components/Shared/Product/selector';
import Price from 'components/Shared/Price';
import { StarRating } from 'components/Shared/StarRating';
import ResponsiveImage from 'components/Shared/Image/ResponsiveImage';

type AppearanceType = 'start' | 'normal' | 'campaign';

interface OwnProps extends ListViewProduct {
  appearance?: AppearanceType;
  isCategoryBold?: boolean;
  isSponsored?: boolean;
  isListView?: boolean;
  isSponsoredByListView?: boolean;
  picture: {
    className?: string;
    height: number;
    width: number;
    responsive?: ResponsiveImage;
  };
  className?: string;
  children?: React.ReactNode;
  renderImage?: () => React.ReactNode;
  showMoreColours?: boolean;
  moreColorsImage?: string;
  isTopTested?: boolean;
  onMouseOver?: (e?: React.MouseEvent<HTMLDivElement>) => void;
  onMouseOut?: (e?: React.MouseEvent<HTMLDivElement>) => void;
  isSponsoredAd?: boolean;
}

const noop = emptyFunction;

type ListPriceProps = Pick<PriceModel, 'isFromPrice' | 'listPrice' | 'currencyUnit'> &
  Pick<IEnergyDeclaration, 'efficiencyArrow'> &
  Pick<ListViewProduct, 'productId' | 'isPartPrice' | 'partPriceUnit'> & {
    onClose: () => void;
  };

interface OwnState {
  svgContent?: string[];
  modalOpen: boolean;
}

const ListPrice = ({
  efficiencyArrow,
  onClose,
  isFromPrice,
  currencyUnit,
  listPrice,
  productId
}: ListPriceProps) => (
  <div className={styles.price}>
    {efficiencyArrow && (
      <EnergyArrow
        productId={productId}
        wrapperClassName={styles.energyArrowWrapper}
        efficiencyArrow={efficiencyArrow}
        className={styles.energyArrow}
        onClose={onClose}
      />
    )}
    {isFromPrice && <span className={styles.listPriceFrom}>fr.</span>}
    <Price
      className={styles.listPrice}
      price={listPrice}
      unit={currencyUnit}
      appearance="discount"
    />
  </div>
);

export class ProductCard extends Component<OwnProps, OwnState> {
  private handleCopy: React.ClipboardEventHandler<HTMLDivElement>;
  constructor(props: OwnProps) {
    super(props);
  }
  public render() {
    const {
      appearance,
      title,
      efficiencyArrow,
      efficiencyDeclarationUrl,
      price,
      className,
      isCampaign,
      isLowPrice,
      children,
      renderImage,
      onMouseOut,
      onMouseOver,
      isNewProduct,
      reviewAvgScore,
      isSponsoredAd,
      isPartPrice,
      partPriceUnit,
      ...link
    } = this.props;
    const { ticket, url } = link;

    return (
      <div
        {...getQaDataAttr(`productcard-${this.props.productId}`)}
        className={cn(styles.item, className, {
          [styles.startPage]: appearance === 'start',
          [styles.normal]: appearance === undefined || appearance === 'normal',
          [styles.sponsoredByAd]: isSponsoredAd
        })}
        onMouseOver={onMouseOver ? onMouseOver : noop}
        onMouseOut={onMouseOut ? onMouseOut : noop}
        onCopy={this.handleCopy}
      >
        <NotifyLink ticket={ticket} url={url} className={styles.imageLink}>
          {this.renderProductImage()}
        </NotifyLink>
        <div className={styles.content}>
          <div className={styles.title}>
            <NotifyLink ticket={ticket} url={url}>
              {title}
            </NotifyLink>
          </div>
          {reviewAvgScore && reviewAvgScore > 0 ? (
            <StarRating className={styles.reviewStars} score={reviewAvgScore} />
          ) : (
            NO_ELEMENT
          )}
          <ListPrice
            {...{
              listPrice: price.presentationPrice,
              isFromPrice: price.isFromPrice,
              currencyUnit: price.currencyUnit,
              productId: this.props.productId,
              isPartPrice: this.props.isPartPrice,
              partPriceUnit: this.props.partPriceUnit,
              efficiencyArrow,
              onClose: () => this.props.onMouseOut && this.props.onMouseOut()
            }}
          />
          <div className={styles.priceDetails}>
            {efficiencyDeclarationUrl && (
              <a
                target="_blank"
                rel="noreferrer"
                className={styles.energyPdf}
                href={efficiencyDeclarationUrl}
              >
                {rm('product_energydeclaration_title')}
              </a>
            )}
          </div>
          <div>{children}</div>
        </div>
      </div>
    );
  }

  private renderProductImage() {
    const {
      renderImage,
      productImage,
      picture,
      appearance,
      splashTextEnd,
      splashTextMiddle,
      splashTextBottom,
      splashTextTop,
      isCampaign,
      brandLogo,
      isNewProduct,
      showMoreColours,
      moreColorsImage,
      title,
      widgets,
      isLowPrice
    } = this.props;
    const isTopTested = Boolean(getIsTopTested({ widgets })) || this.props.isTopTested;

    const splash = appearance === 'normal' && (
      <Splash
        appearance="small"
        {...{
          splashTextEnd,
          splashTextBottom,
          splashTextMiddle,
          splashTextTop,
          isCampaign,
          isLowPrice
        }}
      />
    );

    const splashWithDiscountOnly = appearance === 'campaign' && (
      <Splash appearance="small" {...{ splashTextMiddle, splashTextTop, isLowPrice }} />
    );

    const brand = appearance === 'normal' && (
      <Brand {...brandLogo} appearance="small" className={styles.productBrand} />
    );
    const newProduct = appearance === 'normal' && isNewProduct && (
      <ProductCardFlag
        {...{
          className: cn(styles.isNewProduct, { [styles.single]: !showMoreColours }),
          imgSrc: newProductImage,
          title: rm('productlist_product_new')
        }}
      />
    );
    const moreColors = showMoreColours && (
      <ProductCardFlag
        {...{
          className: styles.moreColours,
          imgSrc: moreColorsImage,
          title: rm('productlist_product_more_colors')
        }}
      />
    );
    const topTested = appearance === 'normal' && isTopTested && (
      <TopTested className={styles.topTested} />
    );

    return (
      <Fragment>
        {renderImage && renderImage()}
        <Image
          {...{
            ...productImage,
            ...picture
          }}
          responsive={picture.responsive}
          className={cn(styles.image, picture.className)}
          paddingBottom
          alt={title}
        />
        {splash}
        {splashWithDiscountOnly}
        {brand}
        {newProduct}
        {moreColors}
        {topTested}
      </Fragment>
    );
  }
}
